import { computed, readonly, ref } from "vue";

/**
 * Thème de l'interface : clair, sombre ou celui du système.
 *
 * Le choix est conservé dans le navigateur (localStorage, donnée non sensible) et appliqué
 * en posant la classe « dark » sur <html>. public/index.html applique déjà le thème avant
 * le chargement de Vue pour éviter un flash blanc.
 */

const STORAGE_KEY = "ibacy-theme";
export const THEME_MODES = [
  { value: "light", label: "Clair", icon: "fas fa-sun" },
  { value: "dark", label: "Sombre", icon: "fas fa-moon" },
  { value: "system", label: "Système", icon: "fas fa-desktop" },
];

const media = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;

const readStored = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return THEME_MODES.some((m) => m.value === value) ? value : "system";
  } catch {
    return "system";
  }
};

const mode = ref(readStored());
const systemDark = ref(media ? media.matches : false);
const resolved = computed(() => (mode.value === "system" ? (systemDark.value ? "dark" : "light") : mode.value));

let transitionTimer = null;

const apply = (animate = false) => {
  const root = document.documentElement;
  if (animate) {
    root.classList.add("theme-transition");
    clearTimeout(transitionTimer);
    transitionTimer = setTimeout(() => root.classList.remove("theme-transition"), 300);
  }
  root.classList.toggle("dark", resolved.value === "dark");
  root.style.colorScheme = resolved.value;
  root.dataset.theme = mode.value;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", resolved.value === "dark" ? "#0d0d10" : "#ffffff");
};

let initialized = false;

/** Applique le thème au démarrage et suit les changements du système. */
export const initTheme = () => {
  if (initialized || typeof document === "undefined") return;
  initialized = true;
  apply();

  media?.addEventListener?.("change", (event) => {
    systemDark.value = event.matches;
    if (mode.value === "system") apply(true);
  });

  // Synchronise les autres onglets ouverts.
  window.addEventListener("storage", (event) => {
    if (event.key === STORAGE_KEY) {
      mode.value = readStored();
      apply(true);
    }
  });
};

export const useTheme = () => {
  const setMode = (value) => {
    if (!THEME_MODES.some((m) => m.value === value)) return;
    mode.value = value;
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* stockage indisponible : le choix vaut pour la session */
    }
    apply(true);
  };

  const current = computed(() => THEME_MODES.find((m) => m.value === mode.value));
  const icon = computed(() => (mode.value === "system" ? (resolved.value === "dark" ? "fas fa-moon" : "fas fa-sun") : current.value.icon));

  return { mode: readonly(mode), resolved, isDark: computed(() => resolved.value === "dark"), current, icon, modes: THEME_MODES, setMode };
};
